import React from "react";
import ConnectedThemeProvider from "@configuratorware/configurator-frontendgui/App/Screens/Configurator/ThemeProvider";
import { merge } from "lodash";
import "../../Styles.scss";

const primary = "#BFB8AF";
const primaryText = "#000000";
const appbarHeight = 60;
const breakpoints = {
  xs: 0,
  sm: 568, // iPhone 5 problematic (width = 568 < 600..)
  md: 960,
  lg: 1280,
  xl: 1920,
};

const mobileAndTabletCheck = () => {
  const { userAgent } = navigator;

  const userAgentPart = userAgent.substr(0, 4);
  if (
    [
      "iPad Simulator",
      "iPhone Simulator",
      "iPod Simulator",
      "iPad",
      "iPhone",
      "iPod",
      "MacIntel",
    ].includes(navigator.platform) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  ) {
    return true;
  }
  return (
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|primaryberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
      userAgent
    ) ||
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
      userAgentPart
    )
  );
};
const customTheme = {
  palette: {
    primary: {
      main: primary,
      text: "#ffffff",
      hover: "#959595",
      pressed: "#585858",
      pressedText: "#ffffff",
      hoverText: "#ffffff",
      lightText: "#000000",
      light: "#ffffff",
      dark: primary,
      darkText: "#000000",
      darkHover: "#F2F2F2",
      darkPressed: "#C9C9C9",
    },
    secondary: {
      main: "#41B702",
      text: "#ffffff",
      light: "#81c784",
      dark: "#388e3c",
      lightText: "lightgreen",
    },
    configBtn: {
      lightText: "#000000",
      light: "#ffffff",
      dark: primary,
      darkText: "#000000",
      lightHover: "#000000",
      lightPressed: "#BFB8AF",
      hover: "#000000",
      pressed: "#585858",
      borderColor: primary,
      pressedText: primary,
    },
    menuBtn: {
      bg: primary,
      text: "#000000",
      pressed: "#BFB8AF",
    },
    backgroundColor: "#FBFBFB",
    productList: {
      // hover: "#959595",
      hoverText: "#000000",
    },
  },
  layout: {
    sidebar: {
      // width: 0,
      width: 350,
      opacity: 1,
      display: "block",
    },
    appbar: {
      heightXL: appbarHeight * 1.5,
      height: appbarHeight,
      heightSm: 47,
    },
    footerbar: {
      height: appbarHeight,
      amountPriceButtonHeight: appbarHeight * 0.7,
      heightSm: 47,
      amountPriceButtonHeightSm: 47,
    },
    configurationpicker: {
      heightXL: 70,
      height: 47,
    },
    productsList: {
      heightXL: 40,
      height: 47,
    },
    resetrow: {
      height: 20,
    },
    buttonIcon: {
      paddingLeft: 10,
      paddingRight: 10,
    },
    radio: {
      color: "primary",
    },
    list: {
      backgroundColor: "#E3E3E3",
      backgroundColorSelected: "#C9C9C9",
    },
  },
  spacing: 5,
  spacingMultiplicator: {
    md: 2,
    lg: 2,
    xl: 3,
  },
  typography: {
    fontFamily: '"Lato", sans-serif',
    h1: {
      color: primaryText,
    },
    h2: {
      color: primaryText,
      fontSize: 14,
      fontWeight: "bold",
    },
    h3: {
      color: primaryText,
    },
    h4: {
      color: primaryText,
    },
    h5: {
      color: primaryText,
    },
    h6: {
      color: primaryText,
    },
    subtitle1: {
      color: primaryText,
    },
    subtitle2: {
      color: primaryText,
    },
    body1: {
      color: primaryText,
      fontSize: 14,
    },
    body2: {
      color: primaryText,
      fontSize: 12,
    },
    button: {
      color: primaryText,
    },
    caption: {
      color: primaryText,
    },
    overline: {
      color: primaryText,
    },
  },
  overrides: {
    ToolBar: {
      background: primary,
      color: "#000000",
    },
  },
};

const overrideTheme = (theme) =>
  merge(theme, {
    palette: {
      primary: {
        main: primary,
        text: "#000000",
        hover: "#585858",
        hoverText: "#000000",
        lightText: "#000000",
        light: "#ffffff",
        lightHover: "#959595",
        lightRipple: "#585858",
        dark: primary,
        darkText: "#000000",
        darkHover: "#C9C9C9",
        darkRipple: "#C9C9C9",
      },
      secondary: {
        main: "#41B702",
        light: "#379506",
        dark: "#388e3c",
        lightText: "lightgreen",
      },
    },

    typography: {
      h1: {
        fontSize: 16,
        [theme.breakpoints.up("sm")]: {
          fontSize: 18,
        },
        [theme.breakpoints.up("xl")]: {
          fontSize: 22,
        },
      },
      h2: {
        fontSize: 14,
        [theme.breakpoints.up("sm")]: {
          fontSize: 18,
        },
        [theme.breakpoints.up("xl")]: {
          fontSize: 22,
        },
      },
      h3: {
        fontSize: 14,
        [theme.breakpoints.up("sm")]: {
          fontSize: 14,
        },
        [theme.breakpoints.up("md")]: {
          fontSize: mobileAndTabletCheck() ? 14 : 16,
        },
        [theme.breakpoints.up("xl")]: {
          fontSize: 18,
        },
      },
      body1: {
        fontSize: 14,
        [theme.breakpoints.up("sm")]: {
          fontSize: 14,
        },
        [theme.breakpoints.up("md")]: {
          fontSize: mobileAndTabletCheck() ? 14 : 16,
        },
        [theme.breakpoints.up("xl")]: {
          fontSize: 16,
        },
      },
      body2: {
        fontSize: 12,
        [theme.breakpoints.up("sm")]: {
          fontSize: 14,
        },
        [theme.breakpoints.up("xl")]: {
          fontSize: 14,
        },
      },
      button: {
        fontSize: 14,
        [theme.breakpoints.up("sm")]: {
          fontSize: 16,
        },
        [theme.breakpoints.up("xl")]: {
          fontSize: 16,
        },
      },
    },

    creator: {
      productPart: {
        color: theme.palette.primary.darkText,
        markerThickness: 2,
        itemSize: "auto",
        minItemSize: 160,
        selectedItemSize: "auto",
      },
    },
    overrides: {
      DefaultLayout: {
        productsList: {
          "&.horizontal": {
            width: "100%",
            background: theme.palette.backgroundColor,
            "& .carousel-wrapper": {
              margin: theme.spacing(0, 6),
              width: `calc(100% - ${theme.spacing(12)}px)`,
              [theme.breakpoints.up("sm")]: {
                margin: theme.spacing(0, 6 * theme.spacingMultiplicator.md),
                width: `calc(100% - ${theme.spacing(
                  12 * theme.spacingMultiplicator.md
                )}px)`,
              },
              [theme.breakpoints.up("xl")]: {
                margin: theme.spacing(0, 4 * theme.spacingMultiplicator.xl),
                width: `calc(100% - ${theme.spacing(
                  8 * theme.spacingMultiplicator.xl
                )}px)`,
              },
              marginBottom: 0,
              [theme.breakpoints.up("sm")]: {
                marginBottom: theme.spacing(7),
              },
              [theme.breakpoints.up("xl")]: {
                marginBottom: theme.spacing(3 * theme.spacingMultiplicator.xl),
              },
              bottom: "auto",
              height: theme.layout.productsList.height,
              maxHeight: theme.layout.productsList.height,
              [theme.breakpoints.up("xl")]: {
                height: theme.layout.productsList.heightXL,
                maxHheight: theme.layout.productsList.heightXL,
              },
            },
          },
        },
        shoppingCart: {
          position: "absolute",
          bottom: 0,
        },
      },
      MuiAppBar: {
        root: {
          background: primary,
          color: "#000000",
          boxShadow: "none",
        },
      },
      MuiToolbar: {
        root: {
          background: primary,
          color: "#000000",
        },
      },
      PopupArea: {
        portalWrapper: {
          zIndex: "1301 !important",
        },
      },

      ColorPalettes: {
        usedColorWrapper: {
          display: "none",
        },
        /* colorPaletteColors: {
        border: 'solid 5px white !important',
        '&:before': {
          background: 'none',
          border: ' 2px solid #707070',
          content: '""',
          display: 'block',
          position: 'relative',
          top: -2,
          left: -2,
          pointerEvents: 'none',
          height: '100%',
          width: '100%',
          borderRadius: '100%',

        },
      },

      selectedColorWrap: {
        boxShadow: 'none !important',
        border: 'solid 5px #C9C9C9 !important',

      }, */
      },

      ProductVariant: {
        ProductVariantImageItem: {
          display: "none !important",
        },
      },

      AmountPrice: {
        closeIcon: {
          borderRadius: 100,
          padding: 4,
          marginTop: -4,
          marginRight: -4,
          "&:hover": {
            boxShadow: "none",
            background: "#F2F2F2",
          },
          "&:active, &:focus": {
            boxShadow: "none",
            background: "#C9C9C9",
          },
        },
      },
      /*
    AcceptDesign:{
      Grid:{
        background:"blue !important",
        display:"flex",

      },
      MuiGrid:{
        background:"blue !important",
        root:{
          background:"green !important",

        }
      },
      checkboxLabel:{
        order:2,
      },
      formHelperText:{
        order:1,
      }
    },*/

      ToolbarList: {
        root: {
          color: theme.palette.primary.darkText,
          height: theme.layout.footerbar.heightSm,
          minHeight: theme.layout.footerbar.heightSm,
          [theme.breakpoints.up("sm")]: {
            height: theme.layout.footerbar.height,
            minHeight: theme.layout.footerbar.height,
          },
        },
        iconHover: {
          fontSize: 18,
          color: theme.palette.primary.darkText,
          height: 37,
          width: 37,
          margin: "5px 9px",
          [theme.breakpoints.up("sm")]: {
            height: 50,
            width: 50,
            margin: "5px 3px",
          },
          [theme.breakpoints.up("md")]: {
            margin: "5px 9px",
          },
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          "&>svg": {
            width: "auto",
            height: 28,
          },
          "& > span": {
            color: theme.palette.primary.text,
          },
          "&:hover": {
            background: "#959595",
            "& > span": {
              color: theme.palette.primary.hoverText,
            },
            cursor: "pointer",
          },
          "&:focus": {
            background: "#585858",
          },
          "&:active": {
            background: "#959595",
          },
        },
      },
      MuiButton: {
        root: {
          borderRadius: 0,
          boxShadow: "none",
          textTransform: "unset",
          "&:hover": {
            boxShadow: "none",
            background: "#959595",
          },
          "&:focus": {
            boxShadow: "none",
            background: "#585858",
          },
          "&:active": {
            background: "#959595",
          },
          "&.Mui-disabled": {
            background: "#EFEFEF",
            color: "#A3A3A3",
            border: "1px solid #A3A3A3",
            "&:hover,&:active,&:focus": {
              opacity: "1 ",
              background: "#EFEFEF !important",
              color: "#A3A3A3  !important",
              border: "1px solid #A3A3A3   !important",
            },
          },
        },

        contained: {
          "&.Mui-disabled": {
            background: "#EFEFEF",
            color: "#A3A3A3",
            border: "1px solid #A3A3A3",
            "&:hover,&:active,&:focus": {
              opacity: "1  ",
              background: "#EFEFEF  ",
              color: "#A3A3A3  ",
              border: "1px solid #A3A3A3  ",
            },
          },
          boxShadow: "none",
          textTransform: "unset",
          "&:hover, &:active, &:focus": {
            boxShadow: "none",
          },
        },
      },
      CustomDialog: {
        closeIcon: {
          borderRadius: 100,
          padding: 4,
          marginTop: -4,
          marginRight: -4,
          "&:hover": {
            boxShadow: "none",
            background: "#F2F2F2",
          },
          "&:active, &:focus": {
            boxShadow: "none",
            background: "#C9C9C9",
          },
        },
      },
      PdfDownload: {
        activeButton: {
          backgroundColor: primary,
        },
      },
      MainButton: {
        root: {
          "&.Mui-disabled": {
            background: "#EFEFEF !important",
            color: "#A3A3A3 !important",
            border: "1px solid #A3A3A3 !important",
            "&:hover,&:active,&:focus": {
              opacity: "1  !important ",
              background: "#EFEFEF  !important ",
              color: "#A3A3A3  !important ",
              border: "1px solid #A3A3A3  !important ",
            },
          },
          background: primary,
          backgroundColor: primary,
          "&:hover": {
            boxShadow: "none !important",
            background: "#959595 !important",
          },
          "&:active, &:focus": {
            boxShadow: "none !important",
            background: "#585858 !important",
          },
        },
      },
    },
    props: {
      // Name of the component ⚛️
      MuiButtonBase: {
        // The properties to apply
        disableRipple: true, // No more ripple, on the whole application 💣!
      },
    },
  });
export default (props) => (
  <ConnectedThemeProvider customTheme={customTheme} withTheme={overrideTheme}>
    {props.children}
  </ConnectedThemeProvider>
);
